import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { create } from 'zustand'

import { THIRTY_MINUTES_IN_MS } from '@/src/general/constants/time'

import { useMeQuery } from '../../auth/services/useMeQuery'
import { intentionApi } from '../api/intentionApi'
import type { TRandomPopularIntentionApi } from '../api/types/getIntentionApi.types'
import { useIntentionStore } from './useIntentionPrayMutation'

type TRewardKeyPrefix = 'community' | 'publication' | 'global'
type TRewardKey = `${TRewardKeyPrefix}:${number}`

type TRewardIntentionsStore = {
  rewardIntentions: {
    [key: TRewardKey]: TRandomPopularIntentionApi | undefined
  }
  addRewardIntention: (rewardKey: TRewardKey, intention: TRandomPopularIntentionApi) => void
}

const useRewardIntentionsStore = create<TRewardIntentionsStore>((set, get) => ({
  rewardIntentions: {},
  addRewardIntention: (rewardKey: TRewardKey, intention: TRandomPopularIntentionApi) =>
    set({
      rewardIntentions: {
        ...get().rewardIntentions,
        [rewardKey]: intention,
      },
    }),
}))

export const useRandomPopularUnprayedIntentionsQuery = (communityId?: number, publicationId?: number) => {
  let rewardKey: TRewardKey
  if (publicationId) rewardKey = `publication:${publicationId}`
  else if (communityId) rewardKey = `community:${communityId}`
  else rewardKey = 'global:0'

  const { data: me } = useMeQuery()
  const { addRewardIntention, rewardIntentions } = useRewardIntentionsStore()
  const rewardIntention = rewardIntentions[rewardKey]
  const prayedIntentions = useIntentionStore((state) => state.prayedIntentions)
  const isPrayed = !!(rewardIntention && prayedIntentions[rewardIntention.id])

  const query = useQuery({
    queryKey: ['intention_random_popular', communityId],
    queryFn: ({ signal }) => intentionApi.getRandomPopularIntentionsQuery({ limit: 10, communityId }, { signal }),
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: THIRTY_MINUTES_IN_MS,
    enabled: !rewardIntention,
  })

  // We don't want to pick an intention that the user neither has already prayed for nor is the owner of
  const unprayedIntentions = query.data?.filter(({ id, user }) => !prayedIntentions[id] && user?.id !== me?.id)

  // Pick a random intention if there is none picked yet
  const isIntentionWaitingToBePicked = !rewardIntention && !!unprayedIntentions && unprayedIntentions.length > 0
  useEffect(() => {
    if (isIntentionWaitingToBePicked) {
      const pickedRewardIntention = unprayedIntentions[Math.floor(Math.random() * unprayedIntentions.length)]
      addRewardIntention(rewardKey, pickedRewardIntention)
    }
  }, [isIntentionWaitingToBePicked, unprayedIntentions, addRewardIntention, rewardKey])

  // Update the intention as prayed if the user prayed for it
  const intention =
    rewardIntention && isPrayed && !rewardIntention.hasPrayed
      ? { ...rewardIntention, hasPrayed: true }
      : rewardIntention

  return { isLoading: query.isLoading || isIntentionWaitingToBePicked, intention }
}
